import type { ReactNode } from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';

import type { FoldedPayer } from '../types';

import { PayerConnectionsFooter } from './PayerConnectionsFooter';
import { PayerRow } from './PayerRow';

export interface PayerTableProps {
  /** Rows already folded and sorted (foldPayers returns them sorted per AC-8). */
  payers: FoldedPayer[];
  liveCount: number;
  unknownCount?: number;
  /** Per-payer action slot, keyed by payerId — GDN-3406 supplies the links (AC-7). */
  renderAction?: (payer: FoldedPayer) => ReactNode;
  footerRightSlot?: ReactNode;
}

/**
 * The clinic Payer Connections table (AC-6, AC-8): header, one PayerRow per
 * non-live payer, and the footer. Presentational only — `payers` is expected
 * pre-sorted by `foldPayers`.
 */
export function PayerTable({
  payers,
  liveCount,
  unknownCount = 0,
  renderAction,
  footerRightSlot,
}: PayerTableProps) {
  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        borderRadius: '8px',
        boxShadow: 1,
        overflow: 'hidden',
      }}
    >
      <Table size="small" aria-label="Payer connections">
        <TableHead>
          <TableRow sx={{ backgroundColor: 'grey.50' }}>
            <TableCell
              sx={{
                fontSize: '11px',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: 'text.secondary',
              }}
            >
              Payer
            </TableCell>
            <TableCell
              sx={{
                fontSize: '11px',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: 'text.secondary',
              }}
            >
              Status
            </TableCell>
            <TableCell
              sx={{
                fontSize: '11px',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: 'text.secondary',
              }}
            >
              What&apos;s happening
            </TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {payers.map(payer => (
            <PayerRow key={payer.payerId} payer={payer} action={renderAction?.(payer)} />
          ))}
        </TableBody>
      </Table>
      <PayerConnectionsFooter
        liveCount={liveCount}
        unknownCount={unknownCount}
        rightSlot={footerRightSlot}
      />
    </Box>
  );
}
